/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { useEffect, useRef } from 'react';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import pearsonTheme from '@greenville/mui-theme';
import Framework, { Container } from '@greenville/framework';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import modules from './modules';
import languages from './common/languages';
import env from './common/env';
import packageJson from '../package.json';
import utils from './common/utils';
import { loginRequest } from './authConfig';
import { callMsGraph } from './graph';
import { initMixpanel } from './modules/aiadmin/utils/mixPanelUtils';

const App = () => {
  const { instance, accounts, inProgress } = useMsal();
  const modulesRef = useRef(null);

  const callLogin = () => {
    const isAuthenticated = accounts.length > 0;
    const sessionData = utils.getSessionData();

    if (inProgress !== InteractionStatus.None) {
      // MSAL is still processing; wait until it's done
      return;
    }

    if (isAuthenticated) {
      // User is already authenticated, no need to redirect
      utils.setCookie();
      const token = utils.getToken();
      callMsGraph(token);
      return;
    }

    if (sessionData) {
      // Try silent SSO if session data exists
      const silentRequest = {
        scopes: ['User.Read'],
        loginHint: sessionData
      };
      instance
        .ssoSilent(silentRequest)
        .then(() => {
          utils.setCookie();
          const token = utils.getToken();
          callMsGraph(token);
        })
        .catch((err) => {
          if (err instanceof InteractionRequiredAuthError) {
            instance.loginRedirect(loginRequest).catch((e) => {
              console.error('Login redirect failed:', e);
            });
          }
        });
    } else {
      // No session data, trigger login redirect
      instance.loginRedirect(loginRequest).catch((e) => {
        console.error('Login redirect failed:', e);
      });
      if (window.location.pathname === '/') {
        Framework.redirectTo('/aiadmin');
      }
    }
  };

  useEffect(() => {
    // Initialize internationalization and modules
    const intlBuilder = Framework.getIntlBuilder();
    languages.forEach((language) => intlBuilder.add(language.locale, language.messages));
    intlBuilder.setDefaultLang('en').addPseudoLocale(env.PSEUDO_LOCALE_CODE).build();

    const moduleBuilder = Framework.getModuleBuilder();
    const localModules = [...modules];
    localModules.forEach((module) => moduleBuilder.add(module));
    modulesRef.current = moduleBuilder.build();

    console.info(`App version - ${packageJson.version}`);
    initMixpanel();
  }, []);

  useEffect(() => {
    callLogin();
  }, [inProgress, accounts]);

  const handleInit = () => {
    // do nothing
  };

  return (
    <ThemeProvider theme={createTheme(pearsonTheme)}>
      <Container
        onInit={handleInit}
        product={env.PRODUCT}
        version={packageJson.version}
        browserCheck={env.BROWSER_CHECK}
        delayedGTM={env.DELAYED_GTM}
        gtmArgs={{
          gtmId: env.GTM_CONTAINER_ID,
          auth: env.GTM_AUTH,
          preview: env.GTM_PREVIEW
        }}
        modules={modulesRef.current}
        correlationIdPrefix={env.CORRELATION_ID_PREFIX}
        authClientId={env.CLIENT_ID}
        partnerEnv={env.ENVIRONMENT}
        authType="INT"
        partnerEnvConfig={{
          INT_BASE_URL: env.INT_BASE_URL,
          INT_USER_PROFILE_URL: env.INT_USER_PROFILE_URL,
          INT_TIMEOUT: env.INT_TIMEOUT,
          INT_AZURE_CLIENT_ID: env.INT_AZURE_CLIENT_ID
        }}
      />
    </ThemeProvider>
  );
};

export default App;
